/* Load before your script containing graphCart(). */
(function (root) {
  'use strict';
  const SURCHARGE_ID = '56474576093526';
  const GROUP = '_surcharge_group';
  const ROLE = '_surcharge_role';
  const isCharge = line => String(line.variant_id || line.id) === SURCHARGE_ID;
  const groupOf = line => line.properties?.[GROUP];
  const isParent = line => !isCharge(line) && line.properties?.[ROLE] === 'product' && groupOf(line);
  const normalId = id => String(id).replace('gid://shopify/ProductVariant/', '');
  function signature(properties) {
    return JSON.stringify(Object.entries(properties || {}).filter(([k,v]) => k !== GROUP && k !== ROLE && v !== '' && v != null)
      .map(([k,v]) => [k, String(v)]).sort(([a],[b]) => a.localeCompare(b)));
  }
  function create(options) {
    const request = options.request;
    let pending = Promise.resolve();
    function serial(work) {
      const next = pending.then(work);
      pending = next.catch(() => {});
      return next;
    }
    async function read() { return request('cart.js'); }
    async function sync() {
      let cart = await read();
      const parents = new Map();
      for (const line of cart.items.filter(isParent)) {
        const id = groupOf(line);
        if (parents.has(id)) throw new Error('Duplicate surcharge group. Refresh the cart and contact support.');
        parents.set(id, line);
      }
      const charges = new Map();
      const updates = {};
      for (const line of cart.items.filter(isCharge)) {
        const id = groupOf(line);
        if (line.properties?.[ROLE] !== 'surcharge' || !parents.has(id) || charges.has(id)) {
          updates[line.key] = 0;
        } else {
          charges.set(id, line);
          if (line.quantity !== parents.get(id).quantity) updates[line.key] = parents.get(id).quantity;
        }
      }
      if (Object.keys(updates).length) {
        await request('cart/update.js', { updates });
        cart = await read();
      }
      const missing = [];
      for (const [id, parent] of parents) {
        if (!charges.has(id)) missing.push({ id: SURCHARGE_ID, quantity: parent.quantity,
          properties: { [GROUP]: id, [ROLE]: 'surcharge' } });
      }
      // Shopify accepts up to 250 items in an add request. No automatic retry.
      for (let offset = 0; offset < missing.length; offset += 250) {
        await request('cart/add.js', { items: missing.slice(offset, offset + 250) });
      }
      cart = await read();
      for (const parent of cart.items.filter(isParent)) {
        const matched = cart.items.filter(line => isCharge(line) && groupOf(line) === groupOf(parent));
        if (matched.length !== 1 || matched[0].quantity !== parent.quantity || matched[0].properties?.[ROLE] !== 'surcharge') {
          throw new Error('Surcharge sync failed. Check the cart before continuing; do not repeat the add blindly.');
        }
      }
      return cart;
    }
    const emit = cart => options.emit?.(cart);
    async function tagsFor(id) {
      const tags = await options.getTags(normalId(id));
      if (!Array.isArray(tags)) throw new Error('Cannot check product tags. Nothing has been added.');
      return tags;
    }
    return {
      surchargeVariantId: SURCHARGE_ID,
      addItem(item) {
        return serial(async () => {
          const id = normalId(item.id);
          if (id === SURCHARGE_ID) throw new Error('Surcharges must be added with their main product.');
          const quantity = Number(item.quantity);
          if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Invalid product quantity');
          const tags = await tagsFor(id);
          const cart = await read();
          let group;
          const properties = { ...(item.properties || {}) };
          delete properties[GROUP]; delete properties[ROLE];
          if (tags.includes('sap') && !tags.includes('sap-surcharge')) {
            const existing = cart.items.find(line => isParent(line) && normalId(line.variant_id || line.id) === id && signature(line.properties) === signature(properties));
            group = existing ? groupOf(existing) : options.randomId();
            properties[GROUP] = group;
            properties[ROLE] = 'product';
          }
          const additions = [{ id, quantity, properties }];
          if (group) additions.push({ id: SURCHARGE_ID, quantity,
            properties: { [GROUP]: group, [ROLE]: 'surcharge' } });
          await request('cart/add.js', { items: additions });
          const updated = await sync();
          const parent = updated.items.find(line => normalId(line.variant_id || line.id) === id &&
            (group ? groupOf(line) === group : signature(line.properties) === signature(properties)));
          if (!parent) throw new Error('Added item could not be located. Check cart before adding again.');
          emit(updated);
          // Existing graphCart callbacks expect ONE product object, not {items:[]}.
          return parent;
        });
      },
      getCart: read,
    };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { create };
  if (!root?.document) return;
  const base = root.Shopify?.routes?.root || '/';
  async function request(route, data) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    try {
      const response = await fetch(base + route, { credentials: 'same-origin', signal: controller.signal,
        method: data === undefined ? 'GET' : 'POST',
        headers: { Accept: 'application/json', ...(data === undefined ? {} : { 'Content-Type': 'application/json' }) },
        ...(data === undefined ? {} : { body: JSON.stringify(data) }) });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message || 'Cart request failed');
      return json;
    } catch (error) {
      // A timed-out POST might already have succeeded. Never auto-retry additions.
      if (error.name === 'AbortError') throw new Error('Cart request timed out. Refresh and check the cart before adding again.');
      throw error;
    } finally { clearTimeout(timeout); }
  }
  const tagCache = new Map();
  async function getTags(id) {
    if (tagCache.has(id)) return tagCache.get(id);
    const tokenValue = typeof storefronttoken !== 'undefined' ? storefronttoken : root.storefronttoken;
    if (!tokenValue) throw new Error('Storefront token is missing; product tags could not be checked.');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    try {
      const response = await fetch('https://rocking-rhino-dev.myshopify.com/api/2026-04/graphql.json', {
        method: 'POST', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': tokenValue },
        body: JSON.stringify({ query: 'query($id:ID!){node(id:$id){... on ProductVariant {product{tags}}}}',
          variables: { id: 'gid://shopify/ProductVariant/' + id } }) });
      const json = await response.json();
      const tags = json.data?.node?.product?.tags;
      if (!response.ok || json.errors?.length || !Array.isArray(tags)) throw new Error('Could not verify product tags. Check Storefront channel publishing and token.');
      tagCache.set(id, tags);
      return tags;
    } finally { clearTimeout(timeout); }
  }
  root.RhinoSurcharge = create({ request, getTags,
    randomId: () => root.crypto.randomUUID(),
    emit: cart => document.dispatchEvent(new CustomEvent('rhino:surcharge-cart-updated', { detail: { cart } })) });
  root.RhinoSurcharge.renderSurchargeRows = async function () {
    const cart = await request('cart.js');
    const container = document.querySelector('.guttercrest-flex-container');
    if (!container) return cart;
    container.querySelectorAll('.rhino-surcharge-row').forEach(row => row.remove());
    const format = cents => new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: cart.currency || 'GBP'
    }).format(cents / 100);
    for (const charge of cart.items.filter(isCharge)) {
      const parent = cart.items.find(line => isParent(line) && groupOf(line) === groupOf(charge));
      if (!parent) continue;
      const row = document.createElement('div');
      row.className = 'sidebar-cart-item rhino-surcharge-row';
      row.dataset.surchargeGroup = groupOf(charge);
      row.dataset.surchargeKey = charge.key;
      const content = document.createElement('div');
      content.className = 'sidebar-title-price-container';
      const title = document.createElement('p');
      title.className = 'name-text';
      title.textContent = charge.product_title + ' — ' + parent.product_title;
      const amount = document.createElement('p');
      amount.className = 'sidebar-line-property';
      amount.textContent = format(charge.final_price) + ' × ' + charge.quantity +
        ' = ' + format(charge.final_line_price);
      content.append(title, amount);
      row.appendChild(content);
      // No independent remove button on a required surcharge.
      const parentRow = document.getElementById(parent.key) || document.getElementById(String(parent.id));
      if (parentRow && container.contains(parentRow)) parentRow.after(row);
      else container.appendChild(row);
    }
    return cart;
  };
})(typeof window !== 'undefined' ? window : null);
